import { InputAdornment } from '@mui/material';
import Loader from 'Loader';
import ArgonBox from 'components/ArgonBox';
import ArgonSelect from 'components/ArgonSelect';
import ArgonTypography from 'components/ArgonTypography';
import React, { useEffect, useState } from 'react';
import { PulseLoader } from 'react-spinners';
import { useAuth } from 'context/AuthContext';
import ArgonInput from 'components/ArgonInput';

const SESSION_STORAGE_KEY = 'selectedOrganization';

export default function SelectOrganization({
  title,
  required,
  showLoader,
  size = 'regular',
  onChange,
  initialOrganizationId,
  initialOrganizationName,
  enableAllOrganizations,
  disabled,
  ...rest
}) {
  const { organizations, isLoading } = useAuth();
  const [selectedOrganization, setSelectedOrganization] = useState({});
  const [formattedOrganizations, setFormattedOrganizations] = useState([]);

  const [showSelector, setShowSelector] = useState(false);

  useEffect(() => {
    // Try to get the previously selected organization from session storage first
    const savedOrganizationData = sessionStorage.getItem(SESSION_STORAGE_KEY);
    console.log(
      'Saved organization data from session storage:',
      savedOrganizationData
    );

    if (savedOrganizationData) {
      try {
        const savedOrg = JSON.parse(savedOrganizationData);
        console.log('Parsed saved organization:', savedOrg);

        // Set the saved organization immediately if it has the required data
        if (savedOrg.value && savedOrg.label) {
          console.log(
            'Restoring organization from session storage immediately:',
            savedOrg
          );
          setSelectedOrganization(savedOrg);
          setShowSelector(true);
          onChange && onChange(savedOrg);
        }
      } catch (error) {
        console.error('Error parsing saved organization data:', error);
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    }

    if (organizations && organizations.length > 0) {
      console.log('User organizations from auth state: ', organizations);

      let temp = organizations.map((org) => ({
        label: org.name || org._id,
        value: org._id
      }));

      // if allowed, add option to select all organizations
      if (enableAllOrganizations)
        temp.unshift({ label: 'All organizations', value: null });

      console.log('Formatted organizations: ', temp);
      setFormattedOrganizations(temp);

      // Priority order for organization selection:
      // 1. initialOrganizationId (if provided)
      // 2. Saved organization from session storage
      // 3. First organization in the list

      if (initialOrganizationId) {
        const initialOrganization = temp.find(
          (org) => org.value === initialOrganizationId.toString()
        );
        console.log(
          'SelectOrganization: initialOrganization found: ',
          initialOrganization
        );

        setShowSelector(true);
        setSelectedOrganization(initialOrganization);
        onChange && onChange(initialOrganization);

        // Save to session storage
        if (initialOrganization?.value) {
          sessionStorage.setItem(
            SESSION_STORAGE_KEY,
            JSON.stringify(initialOrganization)
          );
        }
      } else {
        // If we have a saved organization, verify it still exists in the current organizations
        if (savedOrganizationData) {
          try {
            const savedOrg = JSON.parse(savedOrganizationData);
            const verifiedOrganization = temp.find(
              (org) => String(org.value) === String(savedOrg.value)
            );
            console.log('Found saved organization:', verifiedOrganization);

            if (verifiedOrganization) {
              console.log(
                'Verified saved organization still exists:',
                verifiedOrganization
              );
              setShowSelector(true);
              // Update with the latest data from context (in case name was updated)
              setSelectedOrganization(verifiedOrganization);
              onChange && onChange(verifiedOrganization);
              // Update session storage with latest data
              sessionStorage.setItem(
                SESSION_STORAGE_KEY,
                JSON.stringify(verifiedOrganization)
              );
              return;
            } else {
              console.log(
                'Saved organization not found in current organizations, clearing session storage'
              );
              sessionStorage.removeItem(SESSION_STORAGE_KEY);
            }
          } catch (error) {
            console.error('Error verifying saved organization:', error);
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
          }
        }

        // If no saved organization or saved organization not found, select the first one
        if (temp.length > 0) {
          console.log('Setting first organization as default:', temp[0]);
          setShowSelector(true);
          setSelectedOrganization(temp[0]);
          onChange && onChange(temp[0]);
        }
      }
    }
  }, [organizations, initialOrganizationId, enableAllOrganizations]);

  // useEffect(() => {
  //   setSelectedOrganization(value);
  // }, [value]);

  const handleSetSelectedOrganization = (organization) => {
    console.log('Selected organization: ', organization);
    setSelectedOrganization(organization);
    onChange && onChange(organization);

    // Save the selected organization to session storage with both name and ID
    if (organization?.value) {
      console.log('Saving organization to session storage:', organization);
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(organization));
    } else {
      console.log('Removing organization from session storage');
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  };

  if (showLoader && isLoading) return <Loader />;

  return (
    <>
      <ArgonBox mb={0}>
        {title && (
          <ArgonBox mb={0} ml={0.5}>
            <ArgonTypography variant="caption" fontWeight="bold">
              {title}
              {required && <span style={{ color: 'red' }}>&nbsp;*</span>}
            </ArgonTypography>
          </ArgonBox>
        )}
        {showSelector ? (
          <ArgonSelect
            id="organization-select"
            name="organization-select"
            size={size}
            placeHolder="Select Organization"
            options={formattedOrganizations}
            onChange={handleSetSelectedOrganization}
            value={selectedOrganization}
            overFlow
            isLoading={isLoading}
            isDisabled={disabled}
            {...rest}
            success={selectedOrganization?.value !== null}
          />
        ) : (
          <ArgonInput
            id="organizationName"
            name="organizationName"
            size={size}
            placeHolder="Select Organization"
            value={initialOrganizationName}
            disabled
          />
        )}
      </ArgonBox>
    </>
  );
}
